import { getKeys } from '@morph-mapper/utils';
import { DAG, NodeId } from '../../types';
import { GraphBatchQueryItem, GraphBatchRuntimeState } from './types';

type RuntimeNodeStatus = 'pending' | 'running' | 'ok' | 'error' | 'skipped';

type RuntimeNodeState = {
  status: RuntimeNodeStatus;
  hasFailedChildren: boolean;
};

const createNodeRuntime = (): RuntimeNodeState => {
  return {
    status: 'pending',
    hasFailedChildren: false,
  };
};

const createNodeRuntimes = (dag: DAG): Record<NodeId, RuntimeNodeState> => {
  return getKeys(dag).reduce((acc, nodeId) => {
    acc[nodeId] = createNodeRuntime();
    return acc;
  }, {} as Record<NodeId, RuntimeNodeState>);
};

const computeAffectedSet = (dag: DAG, dirtySet: Set<NodeId>): Set<NodeId> => {
  const affected = new Set<NodeId>(dirtySet);
  for (const nodeId of dirtySet) {
    let current: NodeId | undefined = nodeId;
    while (current) {
      const parent: NodeId | null = dag[current]?.parent;
      if (!parent || affected.has(parent)) break;
      affected.add(parent);
      current = parent;
    }
  }
  return affected;
};

const computeRemainingDeps = (
  dag: DAG,
  affectedSet: Set<NodeId>
): Record<NodeId, number> => {
  const remainingDeps: Record<NodeId, number> = {};
  for (const nodeId of affectedSet) {
    const relation = dag[nodeId];
    if (!relation) continue;
    let count = 0;
    for (const childId of relation.children) {
      if (affectedSet.has(childId)) count += 1;
    }
    remainingDeps[nodeId] = count;
  }
  return remainingDeps;
};

const deriveReadyQueue = (
  dirtySet: Set<NodeId>,
  remainingDeps: Record<NodeId, number>
): NodeId[] => {
  const ready: NodeId[] = [];
  for (const nodeId of dirtySet) {
    if ((remainingDeps[nodeId] ?? 0) === 0) {
      ready.push(nodeId);
    }
  }
  return ready;
};

export type WavePartition = {
  runnable: NodeId[];
  blocked: NodeId[];
};

/**
 * Mutable in-memory state for a single batch execution.
 *
 * Invariants:
 * - `readyQueue` contains nodes with all affected children already resolved.
 * - `remainingDeps[n]` is never negative.
 * - A skipped node will never be re-queued.
 */
export class GraphBatchPipelineInstance {
  readonly batchId: string;
  readonly graphId: string;
  readonly entryId: string;
  readonly graphVersion: number;
  readonly topologyVersion: number;
  readonly dag: DAG;

  private readonly affectedSet: Set<NodeId>;
  private readonly nodes: Record<NodeId, RuntimeNodeState>;
  private readonly remainingDeps: Record<NodeId, number>;
  private readyQueue: NodeId[];
  private inFlight: Set<NodeId>;

  constructor(batch: GraphBatchQueryItem, dag: DAG) {
    const dirtySet = new Set<NodeId>(
      Array.from(batch.dirty).filter((nodeId) => Boolean(dag[nodeId]))
    );

    this.batchId = batch.id;
    this.graphId = batch.graphId;
    this.entryId = batch.entryId;
    this.graphVersion = batch.graphVersion;
    this.topologyVersion = batch.topologyVersion;
    this.dag = dag;

    this.affectedSet = computeAffectedSet(dag, dirtySet);
    this.nodes = createNodeRuntimes(dag);
    this.remainingDeps = computeRemainingDeps(dag, this.affectedSet);
    this.readyQueue = deriveReadyQueue(dirtySet, this.remainingDeps);
    this.inFlight = new Set<NodeId>();
  }

  hasWork(): boolean {
    return this.readyQueue.length > 0;
  }

  takeReadyWave(): NodeId[] {
    const wave = [...this.readyQueue];
    this.readyQueue = [];
    return wave;
  }

  partitionWave(
    wave: NodeId[],
    canRunNode: (nodeId: NodeId) => boolean
  ): WavePartition {
    const runnable: NodeId[] = [];
    const blocked: NodeId[] = [];

    for (const nodeId of wave) {
      if (canRunNode(nodeId)) {
        runnable.push(nodeId);
      } else {
        blocked.push(nodeId);
      }
    }

    return { runnable, blocked };
  }

  markRunning(nodeIds: NodeId[]) {
    nodeIds.forEach((nodeId) => {
      const state = this.nodes[nodeId];
      if (!state) return;
      state.status = 'running';
    });
    this.inFlight = new Set(nodeIds);
  }

  clearInFlight() {
    this.inFlight = new Set();
  }

  markSuccess(nodeId: NodeId): NodeId[] {
    const state = this.nodes[nodeId];
    if (state) {
      state.status = 'ok';
    }

    const parent = this.dag[nodeId]?.parent;
    if (!parent) return [];
    if (!this.affectedSet.has(parent)) return [];

    const next = Math.max(0, (this.remainingDeps[parent] ?? 0) - 1);
    this.remainingDeps[parent] = next;

    if (next !== 0) return [];

    const parentState = this.nodes[parent];
    if (!parentState) return [];
    if (parentState.status === 'skipped') return [];
    if (parentState.hasFailedChildren) return [];
    if (this.inFlight.has(parent)) return [];
    if (this.readyQueue.includes(parent)) return [];

    this.readyQueue.push(parent);
    return [parent];
  }

  markFailure(nodeId: NodeId): NodeId[] {
    const state = this.nodes[nodeId];
    if (state) {
      state.status = 'error';
    }

    return this.skipAncestors(this.dag[nodeId]?.parent ?? null);
  }

  markSkipped(nodeId: NodeId): NodeId[] {
    const skipped: NodeId[] = [];
    let current: NodeId | null = nodeId;

    while (current) {
      const state = this.nodes[current];
      if (!state) break;
      if (state.status === 'skipped') break;

      state.status = 'skipped';
      state.hasFailedChildren = true;
      this.removeFromReady(current);
      skipped.push(current);
      current = this.dag[current]?.parent ?? null;
    }

    return skipped;
  }

  snapshot(): GraphBatchRuntimeState {
    return {
      status: getKeys(this.nodes).reduce((acc, nodeId) => {
        acc[nodeId] = this.nodes[nodeId].status;
        return acc;
      }, {} as GraphBatchRuntimeState['status']),
    };
  }

  private skipAncestors(parent: NodeId | null): NodeId[] {
    const skipped: NodeId[] = [];
    let current = parent;

    while (current) {
      const state = this.nodes[current];
      if (!state) break;
      if (state.status === 'skipped') break;

      state.status = 'skipped';
      state.hasFailedChildren = true;
      this.removeFromReady(current);
      skipped.push(current);
      current = this.dag[current]?.parent ?? null;
    }

    return skipped;
  }

  private removeFromReady(nodeId: NodeId) {
    this.readyQueue = this.readyQueue.filter((readyId) => readyId !== nodeId);
  }
}
